import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import PerformerAvatar from "@/app/admin/_common/PerformerAvatar";
import { formatUtcDate } from "@/app/_utils/dayjs";
import type { Performer } from "@/app/_services/PerformerService";
import { cn } from "@/app/_utils/cn";
import { MoreHorizontal, Pencil, Trash2, Drama, Mail } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

export default function PerformerList({
  performers,
  onEdit,
  onDelete,
  onViewShows,
}: {
  performers: Performer[];
  onEdit: (performer: Performer) => void;
  onDelete: (performer: Performer) => void;
  onViewShows: (performer: Performer) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<Performer | null>(null);
  const isMobile = useIsMobile();

  const actionsMenu = (performer: Performer) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="size-4 shrink-0" size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onEdit(performer)}>
          <Pencil className="mr-2 size-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600 focus:bg-red-50 focus:text-red-700"
          onClick={() => setPendingDelete(performer)}
        >
          <Trash2 className="mr-2 size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col bg-card">
        {performers.length === 0 ? (
          <div className="flex min-h-[400px] flex-col">
            <CuoEmpty icon={Drama} text="No performers" />
          </div>
        ) : (
          <div className="flex flex-col">
            {performers.map((performer) => (
              <div
                key={performer.id}
                className="flex cursor-pointer items-center gap-2.5 border-b border-border px-4 py-2.5 last:border-0"
                onClick={() => onViewShows(performer)}
              >
                <PerformerAvatar
                  src={performer.img_url}
                  firstName={performer.first_name}
                  lastName={performer.last_name}
                  style={{ height: 40, width: 40, fontSize: 14 }}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="truncate text-[14px] font-medium text-foreground">
                    {performer.first_name} {performer.last_name}
                  </div>
                  <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                    <Mail className="size-3.5 flex-none" />
                    {performer.email ? (
                      <a
                        href={`mailto:${performer.email}`}
                        className="truncate hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {performer.email}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <span className="flex-none text-[12.5px] text-muted-foreground">
                  {performer.shows_count ?? 0} {performer.shows_count === 1 ? "show" : "shows"}
                </span>
                <div onClick={(e) => e.stopPropagation()}>{actionsMenu(performer)}</div>
              </div>
            ))}
          </div>
        )}

        <CuoConfirmDialog
          open={pendingDelete !== null}
          onClose={() => setPendingDelete(null)}
          title="Delete performer"
          message="This action cannot be undone. Are you sure you want to delete this performer?"
          onConfirm={async () => {
            if (pendingDelete) await onDelete(pendingDelete);
          }}
          icon={<Trash2 />}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[900px] table-fixed", performers.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-1/5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Name
            </TableHead>
            <TableHead className="h-8 w-14 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Shows
            </TableHead>
            <TableHead className="h-8 w-1/5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Email
            </TableHead>
            <TableHead className="h-8 w-[15%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Instagram
            </TableHead>
            <TableHead className="h-8 w-[15%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Phone
            </TableHead>
            <TableHead className="h-8 w-[15%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Created
            </TableHead>
            <TableHead className="h-8 w-12 pr-3 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {performers.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={7} className="h-full p-0">
                <CuoEmpty icon={Drama} text="No performers" />
              </TableCell>
            </TableRow>
          ) : (
            performers.map((performer) => (
            <TableRow key={performer.id}>
              <TableCell className="py-1.5">
                <div className="flex min-w-0 items-center gap-2.5">
                  <PerformerAvatar
                    src={performer.img_url}
                    firstName={performer.first_name}
                    lastName={performer.last_name}
                    style={{ height: 30, width: 30, fontSize: 11 }}
                  />
                  <span
                    className="cursor-pointer truncate text-[13px] text-foreground hover:underline"
                    onClick={() => onViewShows(performer)}
                  >
                    {performer.first_name} {performer.last_name}
                  </span>
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap py-1.5 text-center text-[12.5px] text-muted-foreground">
                {performer.shows_count ?? 0}
              </TableCell>
              <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                {performer.email ? (
                  <a
                    href={`mailto:${performer.email}`}
                    className="text-foreground hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {performer.email}
                  </a>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                {performer.instagram || "-"}
              </TableCell>
              <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                {performer.phone || "-"}
              </TableCell>
              <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                {formatUtcDate(performer.created_at)}
              </TableCell>
              <TableCell className="py-1.5 pr-3 text-right">{actionsMenu(performer)}</TableCell>
            </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <CuoConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete performer"
        message="This action cannot be undone. Are you sure you want to delete this performer?"
        onConfirm={async () => {
          if (pendingDelete) await onDelete(pendingDelete);
        }}
        icon={<Trash2 />}
      />
    </div>
  );
}
